'use client';

import { Star, Target, Users } from 'lucide-react';
import type { KpiAssignment, Evaluation360Result, KpiAssessmentStatus } from '@/services/api/performance';
import type { TranslationKeys } from '@/locales';

interface PerformanceResultDetailProps {
  assignments: KpiAssignment[];
  e360Summary?: Evaluation360Result | null;
  selfComment?: string;
  managerComment?: string;
  t: TranslationKeys;
}

/**
 * 绩效结果只读展示组件
 * 适用于评估完成后的所有阶段（校准、确认、已完成、归档）
 * 样式完全复用团队 KPI 展开明细的风格
 */
export function PerformanceResultDetail({
  assignments,
  e360Summary,
  selfComment,
  managerComment,
  t,
}: PerformanceResultDetailProps) {
  const kpiT = (t.performance as any)?.kpi;
  const tableT = (t.performance as any)?.kpi?.table;

  return (
    <div className="space-y-4">
      {/* 整体评语 */}
      {(managerComment || selfComment) && (
        <div className="space-y-3">
          {managerComment && (
            <div className="px-4 py-3 rounded-lg" style={{ backgroundColor: '#f7f8fa', border: '1px solid #f2f3f5' }}>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(245,158,11,0.1)' }}>
                  <span className="text-[10px] font-bold" style={{ color: '#f59e0b' }}>管</span>
                </div>
                <span className="text-xs font-semibold" style={{ color: '#1f2329' }}>主管整体评价</span>
              </div>
              <p className="text-sm leading-relaxed" style={{ color: '#374151' }}>{managerComment}</p>
            </div>
          )}
          {selfComment && (
            <div className="px-4 py-3 rounded-lg" style={{ backgroundColor: '#f7f8fa', border: '1px solid #f2f3f5' }}>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(51,112,255,0.1)' }}>
                  <span className="text-[10px] font-bold" style={{ color: '#3370ff' }}>自</span>
                </div>
                <span className="text-xs font-semibold" style={{ color: '#1f2329' }}>整体自评</span>
              </div>
              <p className="text-sm leading-relaxed" style={{ color: '#374151' }}>{selfComment}</p>
            </div>
          )}
        </div>
      )}

      {/* 360 评估维度 */}
      {e360Summary?.dimensionScores && e360Summary.dimensionScores.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Star className="w-4 h-4" style={{ color: '#f59e0b' }} />
            <span className="text-xs font-semibold" style={{ color: '#1f2329' }}>360 评估维度</span>
            {e360Summary.aggregateScore != null && (
              <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(245,158,11,0.1)', color: '#f59e0b' }}>
                综合分 {e360Summary.aggregateScore}
              </span>
            )}
          </div>
          <div className="grid grid-cols-5 gap-2">
            {e360Summary.dimensionScores.map((dim) => (
              <div key={dim.dimension} className="text-center px-2 py-2 rounded-md" style={{ backgroundColor: '#fafbfc', border: '1px solid #f2f3f5' }}>
                <div className="text-[11px] mb-1" style={{ color: '#8f959e' }}>{dim.dimension}</div>
                <div className="text-sm font-semibold" style={{ color: '#1f2329' }}>{dim.avgScore}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* KPI 明细 — 完全复用团队 KPI KpiItem 的只读样式 */}
      {assignments.length > 0 && (
        <div className="divide-y divide-[#f2f3f5]">
          {assignments.map((a) => {
            const assessmentStatus = a.assessment?.status as KpiAssessmentStatus | undefined;
            const statusColor = assessmentStatus ? getStatusStyle(assessmentStatus) : null;

            return (
              <div key={a.id} className="py-4">
                {/* 头部：图标 + 名称 + 权重 + 状态 */}
                <div className="flex items-center gap-2 min-w-0">
                  <Target className="w-4 h-4 shrink-0" style={{ color: '#3370ff' }} />
                  <span className="text-sm font-medium truncate" style={{ color: '#1f2329' }}>
                    {a.name || kpiT?.unnamedIndicator || '未命名指标'}
                  </span>
                  <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: 'rgba(51, 112, 255, 0.08)', color: '#3370ff' }}>
                    {a.weight ?? 0}%
                  </span>
                </div>

                {/* 目标 */}
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                  <div>
                    <span className="text-[11px] font-medium" style={{ color: '#8f959e' }}>{tableT?.baseTarget || '基本目标'}</span>
                    <p className="text-sm mt-0.5" style={{ color: a.baseTarget ? '#1f2329' : '#c9cdd4' }}>
                      {a.baseTarget || '—'}
                    </p>
                  </div>
                  <div>
                    <span className="text-[11px] font-medium" style={{ color: '#8f959e' }}>{tableT?.stretchTarget || '挑战目标'}</span>
                    <p className="text-sm mt-0.5" style={{ color: a.stretchTarget ? '#1f2329' : '#c9cdd4' }}>
                      {a.stretchTarget || '—'}
                    </p>
                  </div>
                </div>

                {/* 依赖信息 */}
                {a.dependentUser && (
                  <div className="mt-2 flex items-center gap-4 px-3 py-2 rounded-md" style={{ backgroundColor: '#f7f8fa' }}>
                    <div className="flex items-center gap-1.5">
                      <Users className="w-3.5 h-3.5" style={{ color: '#8f959e' }} />
                      <span className="text-xs font-medium" style={{ color: '#8f959e' }}>依赖:</span>
                      <span className="text-xs" style={{ color: '#1f2329' }}>{a.dependentUser.displayName}</span>
                    </div>
                    {a.dependencyDescription && (
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs" style={{ color: '#8f959e' }}>|</span>
                        <span className="text-xs" style={{ color: '#646a73' }}>{a.dependencyDescription}</span>
                      </div>
                    )}
                    {a.dependencyStatus && (
                      <span className="text-[11px] px-2 py-0.5 rounded" style={{
                        backgroundColor: a.dependencyStatus === 'CONFIRMED' ? 'rgba(0,180,42,0.08)' : a.dependencyStatus === 'REJECTED' ? 'rgba(245,63,63,0.08)' : 'rgba(255,125,0,0.08)',
                        color: a.dependencyStatus === 'CONFIRMED' ? '#00b42a' : a.dependencyStatus === 'REJECTED' ? '#f53f3f' : '#ff7d00',
                      }}>
                        {a.dependencyStatus === 'CONFIRMED' ? '已确认' : a.dependencyStatus === 'REJECTED' ? '已拒绝' : '待确认'}
                      </span>
                    )}
                  </div>
                )}

                {/* 自评 + 主管评估 并排 */}
                {a.assessment && (
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <div className="px-3 py-2.5 rounded-md" style={{ backgroundColor: '#f7f8fa' }}>
                      <div className="text-[11px] font-medium mb-1.5" style={{ color: '#8f959e' }}>员工自评</div>
                      {a.assessment.selfScore != null ? (
                        <div className="space-y-1">
                          <div className="text-sm" style={{ color: '#374151' }}>
                            {a.assessment.selfComment || <span style={{ color: '#c9cdd4' }}>未填写完成情况</span>}
                          </div>
                          <div className="flex items-center gap-2 text-xs">
                            <span style={{ color: '#8f959e' }}>自评分</span>
                            <span className="font-semibold" style={{ color: '#1f2329' }}>{a.assessment.selfScore}</span>
                            <span style={{ color: '#8f959e' }}>/ 100</span>
                          </div>
                        </div>
                      ) : (
                        <div className="text-sm" style={{ color: '#c9cdd4' }}>员工尚未自评</div>
                      )}
                    </div>
                    <div className="px-3 py-2.5 rounded-md" style={{ backgroundColor: '#f7f8fa' }}>
                      <div className="text-[11px] font-medium mb-1.5" style={{ color: '#8f959e' }}>主管评估</div>
                      {a.assessment.managerScore != null ? (
                        <div className="space-y-1">
                          <div className="text-sm" style={{ color: '#374151' }}>
                            {a.assessment.managerComment || <span style={{ color: '#c9cdd4' }}>未填写评价</span>}
                          </div>
                          <div className="flex items-center gap-2 text-xs">
                            <span style={{ color: '#8f959e' }}>评分</span>
                            <span className="font-semibold" style={{ color: '#1f2329' }}>{a.assessment.managerScore}</span>
                            <span style={{ color: '#8f959e' }}>/ 100</span>
                          </div>
                        </div>
                      ) : (
                        <div className="text-sm" style={{ color: '#c9cdd4' }}>主管尚未评估</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function getStatusStyle(status: string): { color: string; bg: string; label: string } {
  switch (status) {
    case 'SELF_EVALUATED': return { color: '#3370ff', bg: 'rgba(51,112,255,0.08)', label: '已自评' };
    case 'MANAGER_EVALUATED': return { color: '#00b42a', bg: 'rgba(0,180,42,0.08)', label: '主管已评' };
    case 'CONFIRMED': return { color: '#00b42a', bg: 'rgba(0,180,42,0.08)', label: '已确认' };
    default: return { color: '#8f959e', bg: '#f2f3f5', label: '待评估' };
  }
}
